import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { generateBlurhash } from "@/lib/blurhash-utils";
import { toast } from "sonner";

export function useImageUpload(uploadPath?: string) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateUploadUrl = useMutation(api.r2.generateImageUploadUrl);
  const saveImageMetadata = useMutation(api.r2.saveImageMetadata);

  const upload = async (
    file: File,
    imageType: string
  ): Promise<{
    key: string;
    filename: string;
    contentType: string;
    size: number;
    blurhash?: string;
  }> => {
    if (!uploadPath) {
      throw new Error("Upload path is required for image upload");
    }

    setIsUploading(true);
    setError(null);

    try {
      // generate upload URL
      const { url, key } = await generateUploadUrl({
        uploadPath,
        filename: file.name,
        imageType,
      });

      // convert to JPG and upload
      const jpgBlob = await convertToJpg(file);

      // upload to R2
      const uploadResponse = await fetch(url, {
        method: "PUT",
        body: jpgBlob,
        headers: {
          "Content-Type": "image/jpeg",
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      // generate blurhash
      let blurhash: string | undefined;
      try {
        // convert blob to file for blurhash generation
        const jpgFile = new File([jpgBlob], `${key}.jpg`, {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
        blurhash = await generateBlurhash(jpgFile);
      } catch (blurhashError) {
        console.warn("Failed to generate blurhash:", blurhashError);
      }

      // save metadata
      const metadata = await saveImageMetadata({
        key,
        filename: file.name,
        contentType: "image/jpeg",
        size: jpgBlob.size,
        blurhash,
      });

      toast.success(`${imageType} image uploaded successfully`);

      return metadata;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Upload failed";
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  const convertToJpg = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // set canvas size
        canvas.width = img.width;
        canvas.height = img.height;

        // draw image on canvas
        ctx?.drawImage(img, 0, 0);

        // convert to JPG blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Failed to convert image to JPG"));
            }
          },
          "image/jpeg",
          0.9 // quality
        );
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = URL.createObjectURL(file);
    });
  };

  return {
    upload,
    isUploading,
    error,
  };
}
